import type { CategoryMapping, Survey } from '@engagement-web-app/common';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { EmbeddedWarningToast } from '@/components/Toasts';
import * as sharedStyles from '../../index.css';
import { MappingField } from '../MappingField';
import * as styles from './index.css';

// ─── Constants ───────────────────────────────────────────────────────────────

const MAPPING_DESCRIPTION =
    '今年のカテゴリーを前回のサーベイのカテゴリーと紐づけることで、分析ページで正確な経年比較ができるようになります。初期状態では、名前が完全に一致するカテゴリー同士が自動的に紐づけられています。「新規」のままになっているカテゴリーは、今年新しく追加されたものとして扱われ、前回との比較はできません。また、ここでカテゴリーの紐づけを変更すると、そのカテゴリーに含まれる設問のマッピングもすべてリセットされ、あらためて紐づけし直す必要があります。';

// ─── Types ───────────────────────────────────────────────────────────────────

type SurveyCategory = Survey['categories'][number];

type CategoryPair = {
    current: SurveyCategory;
    previousId: string | null;
};

export type CategoryMappingFormData = {
    pairs: CategoryPair[];
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pairCategories(current: SurveyCategory[], mappings: CategoryMapping[]): CategoryPair[] {
    const previousIdByCurrent = new Map(mappings.map((m) => [m.currentCategoryId, m.previousCategoryId]));
    return current.map((cat) => ({
        current: cat,
        previousId: previousIdByCurrent.get(cat.categoryId) ?? null,
    }));
}

// ─── Components ──────────────────────────────────────────────────────────────

type CategoryListProps = {
    survey: Survey;
};

/**
 * Displays a simple list of categories for a single survey without any mapping functionality
 * (used when there is no previous survey to compare to).
 */
export function CategoryList({ survey }: CategoryListProps) {
    return (
        <Flex direction="column" gap="6">
            <Flex direction="column" gap="2">
                <p className={sharedStyles.descriptionText}>{MAPPING_DESCRIPTION}</p>

                <EmbeddedWarningToast
                    title="前回のサーベイがありません"
                    message="前回のサーベイがないため、カテゴリーマッピングはスキップされます。"
                />

                <span className={sharedStyles.itemCount}>カテゴリー {survey.categories.length}件</span>
            </Flex>

            <Flex as="ul" direction="column" gap="3">
                {survey.categories.map((category) => (
                    <li key={category.categoryId}>
                        <span className={styles.categoryName}>{category.name}</span>
                    </li>
                ))}
            </Flex>
        </Flex>
    );
}

type CategoryMappingListProps = {
    organizationId: string;
    currentSurvey: Survey;
    previousSurvey: Survey;
    mappings: CategoryMapping[];
    onSaveMappings: (previousSurveyId: string, formData: CategoryMappingFormData) => Promise<void>;
};

/**
 * Displays a list of category pairs for the current and previous surveys,
 * allowing the user to map categories between them.
 */
export function CategoryMappingList({
    currentSurvey,
    previousSurvey,
    mappings,
    onSaveMappings,
}: CategoryMappingListProps) {
    const [hideMatched, setHideMatched] = useState(false);

    const methods = useForm<CategoryMappingFormData>({ defaultValues: { pairs: [] } });

    const {
        watch,
        handleSubmit,
        formState: { isDirty, isSubmitting },
        reset,
    } = methods;

    useEffect(() => {
        reset({ pairs: pairCategories(currentSurvey.categories, mappings) });
    }, [currentSurvey, mappings, reset]);

    const handleSaveMappings = async (formValues: CategoryMappingFormData) => {
        await onSaveMappings(previousSurvey.surveyId, formValues);
        reset(formValues);
    };

    const pairs = watch('pairs');

    const previousCategoryOptions = previousSurvey.categories.map((cat) => ({ id: cat.categoryId, label: cat.name }));

    return (
        <FormProvider {...methods}>
            <Flex direction="column" gap="8" as={Form.Root} onSubmit={handleSubmit(handleSaveMappings)}>
                <Flex direction="column" gap="2">
                    <p className={sharedStyles.descriptionText}>{MAPPING_DESCRIPTION}</p>

                    {/* TODO: Properly implement checkbox with label */}
                    <label htmlFor="hideMatched" className={styles.hideMatchedCheckbox}>
                        <Checkbox
                            id="hideMatched"
                            checked={hideMatched}
                            onCheckedChange={(checked) => setHideMatched(checked === true)}
                        />
                        <span>新規・一致していない項目のみ表示</span>
                    </label>

                    <span className={sharedStyles.itemCount}>カテゴリー {pairs.length}件</span>
                </Flex>

                <Flex as="ul" direction="column" gap="4">
                    {pairs.map((pair, index) => {
                        if (hideMatched && pair.previousId) return null;
                        return (
                            <li key={`${pair.current.categoryId}-${pair.previousId ?? 'null'}`}>
                                <MappingField
                                    label={pair.current.name}
                                    index={index}
                                    previousId={pair.previousId}
                                    previousOptions={previousCategoryOptions}
                                />
                            </li>
                        );
                    })}
                </Flex>

                <Flex justify="space-between" align="center">
                    <Flex direction="column" gap="2">
                        <p className={sharedStyles.saveNoticeTitle}>カテゴリーマッピングの設定の保存</p>
                        <p className={sharedStyles.saveNoticeDescription}>
                            こちらのカテゴリーマッピングの設定を保存すると、分析ページで正確な経年比較が表示されます。
                        </p>
                    </Flex>

                    <Button type="submit" disabled={!isDirty || isSubmitting}>
                        <Icon icon={Save} size="sm" />
                        カテゴリーのマッピングを保存する
                    </Button>
                </Flex>
            </Flex>
        </FormProvider>
    );
}

/**
 * Shares its shape with `AttributeMappingList` (both render a list of `MappingField` rows),
 * so this stands in for either while the mapping/attributes data is still loading.
 */
export function CategoryMappingListSkeleton() {
    return (
        <Flex direction="column" gap="6">
            <Flex direction="column" gap="2">
                {/* Static description; only the item count depends on loaded data. */}
                <p className={sharedStyles.descriptionText}>{MAPPING_DESCRIPTION}</p>
                <Skeleton.Line width={96} height="sm" />
            </Flex>

            <Flex as="ul" direction="column" gap="4">
                {Array.from({ length: 4 }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, rows have no identity
                    <li key={i}>
                        <Form.FieldSkeleton />
                    </li>
                ))}
            </Flex>
        </Flex>
    );
}
